import { ImageWithFallback } from "@/components/ImageWithFallback";
import { CompletedIcon, subscriberOnboardingIcon } from "@/designSystemIcons";
import { trackClubImportCta } from "@/events/clubImportMixpanel";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { updateUser, userIsLoggedInSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import axios from "../../api/privateApi";
import onboardingHeader from "../../assets/onboardingHeader.png";

const toEmbedLink = (link: string) => link.replace("watch?v=", "embed/");

const SubscriberOnboarding = () => {
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { t } = useTranslation("subscription");
  const dispatch = useDispatch<AppDispatch>();
  const userIsLoggedIn = useSelector(userIsLoggedInSelector);

  const stepsCompleted =
    useSelector((s: any) => s.userReducer.user?.completedOnboarding) ?? [];

  const isDone = useCallback(
    (slug: string) => stepsCompleted.includes(slug),
    [stepsCompleted],
  );

  const markDone = useCallback(
    async ({ slug, label }: { slug: string; label: string }) => {
      if (!slug || isDone(slug)) return;

      const previous = stepsCompleted;
      const optimistic = [...previous, slug];

      dispatch(updateUser({ completedOnboarding: optimistic }));

      try {
        const res = await axios.post("/users/completed-onboarding", {
          step: slug,
        });
        if (res?.data?.completedOnboarding) {
          dispatch(
            updateUser({ completedOnboarding: res.data.completedOnboarding }),
          );
        }
        dispatch(
          addAlert({
            title: t("onboarding.subscriber.alerts.completedTitle"),
            description: t(
              "onboarding.subscriber.alerts.completedDescription",
              {
                label,
              },
            ),
            type: "success",
          }),
        );
      } catch {
        dispatch(updateUser({ completedOnboarding: previous }));
        dispatch(
          addAlert({
            title: t("onboarding.subscriber.alerts.completeFailedTitle"),
            description: t(
              "onboarding.subscriber.alerts.completeFailedDescription",
              { label },
            ),
            type: "error",
          }),
        );
      }
    },
    [dispatch, isDone, stepsCompleted, t],
  );

  const markUndone = useCallback(
    async ({ slug, label }: { slug: string; label: string }) => {
      if (!slug || !isDone(slug)) return;

      const previous = stepsCompleted;
      const optimistic = previous.filter((s) => s !== slug);

      dispatch(updateUser({ completedOnboarding: optimistic }));

      try {
        const res = await axios.delete(
          `/users/completed-onboarding/${encodeURIComponent(slug)}`,
        );
        if (res?.data?.completedOnboarding) {
          dispatch(
            updateUser({ completedOnboarding: res.data.completedOnboarding }),
          );
        }
        dispatch(
          addAlert({
            title: t("onboarding.subscriber.alerts.uncheckedTitle"),
            description: t(
              "onboarding.subscriber.alerts.uncheckedDescription",
              {
                label,
              },
            ),
            type: "info",
          }),
        );
      } catch {
        dispatch(updateUser({ completedOnboarding: previous }));
        dispatch(
          addAlert({
            title: t("onboarding.subscriber.alerts.uncheckFailedTitle"),
            description: t(
              "onboarding.subscriber.alerts.uncheckFailedDescription",
              { label },
            ),
            type: "error",
          }),
        );
      }
    },
    [dispatch, isDone, stepsCompleted, t],
  );

  const toggleDone = useCallback(
    ({ slug, label }: { slug: string; label: string }) =>
      isDone(slug) ? markUndone({ slug, label }) : markDone({ slug, label }),
    [isDone, markDone, markUndone],
  );

  return (
    <div className="bg-panel-base text-slate-100 min-h-screen">
      <div className="flex items-center justify-center space-y-10">
        <h1
          className={cn(
            "absolute text-[58px] font-semibold text-dark-primary p-0 m-0",
            isTouch && "text-[28px] m-0 p-0",
          )}
        >
          {t("onboarding.subscriber.title")}
        </h1>
        <ImageWithFallback
          src={onboardingHeader}
          alt={t("onboarding.subscriber.headerAlt")}
          className={cn(
            "block w-full object-cover",
            isTouch ? "h-[140px]" : "h-[260px]",
          )}
        />
      </div>

      {!isTouch && <div className="h-15 bg-panel-middleground w-full" />}

      <header className="max-w-5xl mx-auto px-4 pt-10 text-center space-y-3">
        <p
          className={cn(
            "text-dark-primary text-xl font-semibold",
            isTouch && "text-base",
          )}
        >
          {t("onboarding.subscriber.intro")}
        </p>
      </header>

      <main className="max-w-5xl mx-auto mt-10">
        {subscriberOnboardingData.map((data, index) => {
          const link = data.youtubeLink || "";
          const embedLink = toEmbedLink(link);
          const src = link ? `${embedLink}?rel=0&modestbranding=1` : "";
          const optionalTitle = data.hasOptionalTitle
            ? t("onboarding.subscriber.steps.beyond.title")
            : undefined;
          const label = t(
            `onboarding.subscriber.steps.${data.key}.label` as const,
          );
          const buttonText =
            data.key === "beyond"
              ? undefined
              : t(`onboarding.subscriber.steps.${data.key}.button` as const);

          return (
            <section
              key={index}
              className={cn(
                "flex py-0 px-9 gap-30 h-full odd:bg-new-gray-800 even:bg-new-gray-750",
                index === 0 && "rounded-t-lg",
                index === subscriberOnboardingData.length - 1 && "rounded-b-lg",
                isTouch &&
                  "flex-col p-4 gap-0 h-auto first:rounded-t-none last:rounded-b-none",
              )}
            >
              <div
                className={cn(
                  "flex flex-col w-1/2 justify-between py-10",
                  isTouch && "w-full py-0",
                )}
              >
                <div className="flex flex-col gap-1">
                  <div
                    className={cn(
                      "flex items-center gap-2",
                      isTouch && "justify-between",
                    )}
                  >
                    <span className="font-semibold text-2xl">
                      <span>{optionalTitle ?? index + 1}</span>
                      {isTouch && <span>.</span>}
                    </span>

                    <button
                      type="button"
                      disabled={!userIsLoggedIn}
                      onClick={() =>
                        userIsLoggedIn && toggleDone({ slug: data.slug, label })
                      }
                      className={cn(
                        "max-touch-device:p-2 max-touch-device:-m-2",
                        !userIsLoggedIn && "cursor-not-allowed",
                      )}
                    >
                      <div
                        className={cn(
                          isDone(data.slug)
                            ? "text-complete"
                            : "text-[#545F6D]",
                        )}
                      >
                        <CompletedIcon width="24" height="24" />
                      </div>
                    </button>
                  </div>

                  <h3
                    className={cn(
                      "mb-1 font-semibold text-base text-dark-primary",
                    )}
                  >
                    {label}
                  </h3>
                  <p
                    className={cn("text-sm font-semibold text-dark-secondary")}
                  >
                    {t(
                      `onboarding.subscriber.steps.${data.key}.description` as const,
                    )}
                  </p>
                </div>

                {buttonText && (
                  <NavLink
                    to={
                      data.slug === "import-club"
                        ? isTouch
                          ? "/import-mobile"
                          : "/club-import-tutorial"
                        : data.buttonLink
                    }
                    className={cn(
                      "flex text-dark-tertiary items-center gap-1 mb-4",
                      isTouch && "self-end mt-4 underline-offset-4",
                    )}
                    onClick={() => {
                      if (data.slug === "import-club")
                        trackClubImportCta({
                          section: "Onboarding",
                          subsection: "Subscriber Onboarding",
                        });
                      window.scrollTo(0, 0);
                    }}
                  >
                    <span className="text-sm underline">{buttonText}</span>{" "}
                    <span>{subscriberOnboardingIcon}</span>{" "}
                  </NavLink>
                )}
              </div>

              <div
                className={cn(
                  "flex items-center justify-center w-1/2 py-8",
                  isTouch && "w-full py-8",
                )}
              >
                <div
                  className={cn(
                    "relative w-full overflow-hidden",
                    isTouch ? "h-60 w-[416px]" : "h-60",
                  )}
                >
                  <iframe
                    src={src}
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={
                      optionalTitle
                        ? t("onboarding.subscriber.videoTitleExtra", {
                            title: optionalTitle,
                            step: index + 1,
                          })
                        : t("onboarding.subscriber.videoTitle", {
                            step: index + 1,
                          })
                    }
                  />
                </div>
              </div>
            </section>
          );
        })}
        <footer
          className={cn(
            "text-center mt-10 pb-10 space-y-6 px-10",
            isTouch && "space-y-1",
          )}
        >
          <h2
            className={cn(
              "font-semibold text-[32px] text-dark-primary",
              isTouch && "text-lg",
            )}
          >
            {t("onboarding.subscriber.done")}
          </h2>
          <p
            className={cn("text-lg text-dark-secondary", isTouch && "text-sm")}
          >
            {t("onboarding.subscriber.outro")}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default SubscriberOnboarding;

// "step must be one of the following values: import-club, solve-sbc-with-own-club, find-your-clubs-best-squad, see-best-chains-for-evos, find-hidden-profit-in-club, see-top-upgrades-for-active-squad"

type SubscriberOnBoardingStep =
  | "import-club" // club import
  | "solve-sbc-with-own-club" // sbc solver
  | "find-your-clubs-best-squad" // squad builder
  | "see-best-chains-for-evos" // evo finder
  | "find-hidden-profit-in-club" // evo builder
  | "see-top-upgrades-for-active-squad"; // extra

/*
  Copy lives under subscription:onboarding.subscriber.steps.<key>; the table
  keeps ids and links only and the component reads the text at render.
*/
type SubscriberOnboardingItem = {
  id: number;
  key:
    | "importClub"
    | "solveSbc"
    | "squadBuilder"
    | "evoFinder"
    | "evoBuilder"
    | "beyond";
  hasOptionalTitle?: boolean;
  slug: SubscriberOnBoardingStep;
  isComplete: boolean;
  buttonLink?: string;
  youtubeLink: string;
};

// "step must be one of the following values: import-club, solve-sbc-with-own-club, find-your-clubs-best-squad, see-best-chains-for-evos, find-hidden-profit-in-club, see-top-upgrades-for-active-squad"

export const subscriberOnboardingData: SubscriberOnboardingItem[] = [
  {
    id: 1,
    key: "importClub",
    slug: "import-club",
    isComplete: true,
    buttonLink: "https://www.easysbc.io/club-import-tutorial",
    youtubeLink: "https://www.youtube.com/watch?v=Ik--9UP5mks",
  },
  {
    id: 2,
    key: "solveSbc",
    slug: "solve-sbc-with-own-club",
    isComplete: false,
    buttonLink: "https://www.easysbc.io/",
    youtubeLink: "https://www.youtube.com/watch?v=zxD1thPtbfE",
  },
  {
    id: 3,
    key: "squadBuilder",
    slug: "find-your-clubs-best-squad",
    isComplete: false,
    buttonLink: "https://www.easysbc.io/squad-builder",
    youtubeLink: "https://www.youtube.com/watch?v=NixIoWEnGtY",
  },
  {
    id: 4,
    key: "evoFinder",
    slug: "see-best-chains-for-evos",
    isComplete: false,
    buttonLink: "https://www.easysbc.io/evolutions/players",
    youtubeLink: "https://www.youtube.com/watch?v=_7e2YkfbOhk",
  },
  {
    id: 5,
    key: "evoBuilder",
    slug: "find-hidden-profit-in-club",
    isComplete: false,
    buttonLink: "https://www.easysbc.io/evolutions/builder",
    youtubeLink: "https://www.youtube.com/watch?v=HstKXseEKwQ",
  },
  {
    id: 6,
    key: "beyond",
    hasOptionalTitle: true,
    slug: "see-top-upgrades-for-active-squad",
    isComplete: false,
    youtubeLink: "https://www.youtube.com/watch?v=Qc3SeObg5Xw",
  },
];
